<script setup lang="ts">
import type { VDataTable } from 'vuetify/components';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, watch, nextTick } from 'vue';
import { type Shift, type ShiftWithoutFinancialInformation } from '@/api/shifts';
import { vitalSignRecordsApi, type VitalSignRecord } from '@/api/vital-sign-records';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { useShowVoided } from '@/composables/useShowVoided';
import { clinicalAuthorName } from '@/utils/clinical-author';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalRecordVoid from '@/components/clinical-records/clinical-record-void.vue';
import ClinicalVoidMark from '@/components/clinical-records/clinical-void-mark.vue';
import ShowVoidedSwitch from '@/components/clinical-records/show-voided-switch.vue';
import ShiftVitalSignsSummary from './shift-vital-signs-summary.vue';

const props = defineProps<{ shift: Shift | ShiftWithoutFinancialInformation }>();

const {
  data: vitalSignRecords,
  isLoading: isLoadingVitalSignRecords,
} = useQuery({
  queryKey: ['vitalSignRecords', props.shift.id],
  queryFn: () => vitalSignRecordsApi.indexByShift(props.shift.patientId, props.shift.id),
});

// Anulados show on demand, crossed out; the summary leaves them out.
const { showVoided, voidedCount, visibleItems } = useShowVoided(vitalSignRecords);
const recordsInForce = computed(() => (vitalSignRecords.value ?? []).filter(record => !record.voided));

function correctionKind(record: VitalSignRecord) {
  return record.bloodPressureRecordSystolic ? 'bloodPressure' : 'value';
}

type GroupHeaderSlotProps = Parameters<NonNullable<VDataTable['$slots']['group-header']>>[0];

type StoredGroupHeader = Pick<GroupHeaderSlotProps, 'item' | 'toggleGroup' | 'isGroupOpen'>;

const groupHeaders = ref<Record<string, StoredGroupHeader>>({});

function expandAllGroups() {
  if (groupHeaders.value && Object.keys(groupHeaders.value).length > 0) {
    Object.values(groupHeaders.value).forEach((header) => {
      if (header && header.toggleGroup && header.item) {
        header.toggleGroup(header.item);
      }
    });
  }
}

watch(vitalSignRecords, (records) => {
  if (records && records.length > 0) {
    nextTick(() => {
      expandAllGroups();
    });
  }
}, { immediate: true });

const { tableProps } = useResponsiveTable({ stack: false });

const groupBy = ref([{ key: 'vitalSignName', order: 'asc' }] as const);

const sortBy = ref([{ key: 'recordedAt', order: 'asc' }] as const);

const showSummaryDialog = ref(false);
function openSummaryDialog() {
  showSummaryDialog.value = true;
}

const headers = [
  { title: 'Signo Vital', key: 'data-table-group', align: 'start' },
  { title: 'Valor', key: 'formattedValue', align: 'start' },
  { title: 'Fecha y Hora', key: 'humanRecordedAt', align: 'start' },
  { title: 'Registrado por', key: 'author', align: 'start', sortable: false },
] as const;
</script>
<template>
  <div>
    <v-data-table
      v-bind="tableProps"
      :loading="isLoadingVitalSignRecords"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :group-by="groupBy"
      :sort-by="sortBy"
      :items="visibleItems"
      class="pa-2"
      loading-text="Cargando signos vitales..."
      no-data-text="No hay registros de signos vitales en este turno"
    >
      <template #group-header="{ item, columns, toggleGroup, isGroupOpen }">
        <v-hover v-slot="{ isHovering, props: hoverProps }">
          <tr
            v-bind="hoverProps"
            :ref="el => { if (el)
              groupHeaders[item.value] = { item, toggleGroup, isGroupOpen } }"
          >
            <td
              v-ripple
              :colspan="columns.length"
              :class="{
                'bg-grey-lighten-4': isHovering,
                'bg-grey-lighten-3': isGroupOpen(item) && !isHovering
              }"
              class="cursor-pointer"
              @click="toggleGroup(item)"
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="d-flex align-center ga-1">
                  <span> {{ item.value }} </span>
                  <v-chip
                    color="primary"
                    size="small"
                  >
                    {{ item.items.length }}
                  </v-chip>
                </div>
                <v-btn
                  class="group-arrow"
                  :icon="isGroupOpen(item) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  density="compact"
                  size="small"
                  variant="text"
                />
              </div>
            </td>
          </tr>
        </v-hover>
      </template>
      <template #[`item.humanRecordedAt`]="{ item }">
        <span class="text-no-wrap">{{ item.humanRecordedAt }}</span>
        <clinical-record-history
          item-type="VitalSignRecord"
          :item-id="item.id"
          :edited="item.edited"
          :label="`${item.vitalSignName} · ${item.humanRecordedAt}`"
        />
        <clinical-record-void
          item-type="VitalSignRecord"
          :item-id="item.id"
          :can-void="item.canVoid"
          :label="`${item.vitalSignName} · ${item.humanRecordedAt}`"
          :correction="correctionKind(item)"
        />
      </template>
      <template #[`item.formattedValue`]="{ item }">
        <span :class="{ 'text-decoration-line-through text-medium-emphasis': item.voided }">
          {{ item.formattedValue }}
        </span>
        <clinical-void-mark
          :voided="item.voided"
          :details="item.voidDetails"
        />
      </template>
      <template #[`item.author`]="{ item }">
        {{ clinicalAuthorName(item.author) || '-' }}
      </template>
      <template #top>
        <div class="justify-end align-center d-flex ga-2 pa-2">
          <show-voided-switch
            v-model="showVoided"
            :count="voidedCount"
          />
          <v-tooltip
            v-if="!isLoadingVitalSignRecords"
            text="Ver Resumen"
            location="bottom"
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-bind="activatorProps"
                icon="mdi-heart-pulse"
                variant="outlined"
                color="primary"
                size="small"
                @click="openSummaryDialog"
              />
            </template>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    <shift-vital-signs-summary
      v-model="showSummaryDialog"
      :vital-sign-records="recordsInForce"
    />
  </div>
</template>
<style scoped>
.group-arrow {
  pointer-events: none;
}
</style>
